import { useEnvContext } from "context/EnvContext";
import { useSenderAccountContext } from "context/SenderAccountContext";
import { ARH_XSL_FILENAME, DOCS_FORM_NAMES } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getName } from "core/model/accounts";
import { getStaticAsset } from "core/model/config";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  AuctionForm,
  DocsFormDataMapped,
  DocsFormName,
  RehabFormName,
} from "core/types";
import { transformXmlToPdf } from "dsl/ecosystems/PatientForms/RehabForms/useAHBResponseToPDF";
import { RehabFormDataMapped } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { sanitizePdfFormTextFieldsForStandardFont } from "dsl/ecosystems/PatientForms/utils/sanitizePdfFormTextForStandardFont";
import { getCachedTemplate } from "dsl/ecosystems/PatientForms/utils/pdfTemplateCache";
import { MigratedForms } from "dsl/ecosystems/RecareDocs/Modal/utils";
import { createAHBRequestXml } from "onprem/utils";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { PdfConfig } from "..";

export type CreateFormPdfProps<T extends DocsFormName> = {
  formData: DocsFormDataMapped<T>;
  pdfConfig: PdfConfig<T>;
  documentNumber: number;
};

export type CreateDischargePdfProps = {
  formData: DocsFormDataMapped<MigratedForms>;
};

const formPageRanges: Partial<
  Record<DocsFormName, { endPage: number; startPage: number }>
> = {
  [DOCS_FORM_NAMES.GKV_GENERAL]: { endPage: 4, startPage: 1 },
  [DOCS_FORM_NAMES.GKV_MEDICAL]: { endPage: 10, startPage: 5 },
};

export const useCreateFormPdf = ({
  auctionForms,
  externalId,
  formName,
}: {
  externalId: string;
  auctionForms: AuctionForm[];
  formName: DocsFormName;
}) => {
  const { env } = useEnvContext();
  const { currentLocale: locale } = useDateLocale();
  const { trackEvent } = useTracking();
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const { account } = useSenderAccountContext();
  const createdBy = getName(account, getOntology);

  async function createFormPdf<T extends DocsFormName>({
    documentNumber,
    formData,
    pdfConfig,
  }: CreateFormPdfProps<T>) {
    try {
      if (pdfConfig.pdfFiller) {
        const formUrl = getStaticAsset(pdfConfig.blankPdfAssetPath);
        const formPdfBytes = await getCachedTemplate(formUrl);
        const pdfDoc = await PDFDocument.load(formPdfBytes);
        const form = pdfDoc.getForm();
        pdfConfig.pdfFiller({ form, formData, getOntology, locale });
        sanitizePdfFormTextFieldsForStandardFont(form);

        // burn values into PDF so we dont need to render annotation layers
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(font);

        form.flatten();

        const pdfBytes = await pdfDoc.save();
        return pdfBytes;
      } else if (pdfConfig.isXML) {
        const { schemaVersion, xml } = await createAHBRequestXml({
          auctionForms,
          documentNumber,
          env,
          externalId,
          formData: formData as RehabFormDataMapped<RehabFormName>,
          formName: formName as RehabFormName,
          trackEvent,
        });
        const xslFilename = ARH_XSL_FILENAME[schemaVersion];
        const xslUrl = getStaticAsset(xslFilename);
        const res = await fetch(xslUrl);
        if (!res.ok) throw new Error("Failed to load XSL");
        const xsl = await res.text();

        return transformXmlToPdf({
          xml,
          xsl,
          ...formPageRanges[formName],
        });
      } else if (pdfConfig.pdfCreator) {
        return pdfConfig.pdfCreator({
          createdBy,
          formData,
          getOntology,
          locale,
          translations,
        });
      } else {
        throw new Error("No PDF filler, pdf creator, or XML config found");
      }
    } catch (err) {
      const message = "Error saving PDF";
      console.error(message, err);
      throw new Error(message);
    }
  }

  return createFormPdf;
};
